// src/components/Forecast/ForecastHome.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	Paper,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { GlobalContext } from "../../GlobalContext";
import { listForecasts, deleteForecastFile } from "../../api/forecast.client";
import ImportForecastModal from "./ImportForecastModal";
import EditForecastModal from "./EditForecastModal";
import { auditPost } from "../../api/config";

export default function ForecastHome() {
	const { site_id, getModuleId } = useContext(GlobalContext);
	const { t } = useTranslation();

	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	const [openImport, setOpenImport] = useState(false);
	const [editId, setEditId] = useState(null);

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [toDelete, setToDelete] = useState(null);

	const [selectedForecastId, setSelectedForecastId] = useState(null);

	const reload = async () => {
		if (!site_id) {
			return;
		}
		setLoading(true);
		setErrorMsg("");
		try {
			const data = await listForecasts(site_id);
			setRows(Array.isArray(data) ? data : []);
		} catch (e) {
			setErrorMsg(t("forecast.home.loadError"));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		reload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [site_id]);

	const handleOpenImport = () => {
		if (getModuleId) {
			const id = getModuleId("Forecast", "Crear un registro de demanda");
			if (id && site_id) auditPost(id, site_id);
		}
		setOpenImport(true);
	};

	const handleImported = () => {
		reload();
	};

	const handleEdit = (id) => {
		if (getModuleId) {
			const pid = getModuleId("Forecast", "Actualizar un registro de demanda");
			if (pid && site_id) auditPost(pid, site_id);
		}
		setSelectedForecastId(id);
		setEditId(id);
	};
	const handleCloseEdit = () => setEditId(null);
	const handleEdited = () => reload();

	const askDelete = (row) => {
		setToDelete(row);
		setConfirmOpen(true);
	};
	const cancelDelete = () => {
		setConfirmOpen(false);
		setToDelete(null);
	};
	const confirmDelete = async () => {
		if (!toDelete) {
			return;
		}
		// Auditoría: Eliminar un registro de forecast
		if (getModuleId) {
			const pid = getModuleId("Forecast", "Eliminar un registro de demanda");
			if (pid && site_id) auditPost(pid, site_id);
		}
		try {
			await deleteForecastFile({ site_id, file_id: toDelete.id });
			setRows((prev) => {
				const newRows = prev.filter((r) => r.id !== toDelete.id);
				return newRows;
			});
		} catch (e) {
		} finally {
			cancelDelete();
		}
	};

	const prettyDate = (iso) => {
		const result = iso ? dayjs(iso).format("DD/MM/YYYY") : "";
		return result;
	};

	const orderedRows = useMemo(() => {
		const sorted = [...rows].sort((a, b) =>
			String(a.start_dtm || "").localeCompare(String(b.start_dtm || ""))
		);
		return sorted;
	}, [rows]);

	return (
		<Box
			sx={{
				width: "100%",
				p: 2,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
			}}
		>
			{/* Título centrado como en Audit */}
			<Typography variant="h5" align="center" sx={{ mb: 1.5 }}>
				{t("forecast.home.title")}
			</Typography>

			{/* Contenedor centrado con ancho controlado */}
			<Box
				sx={{
					width: { xs: "100%", md: "75%" }, // mismo criterio del ejemplo
					p: 2,
				}}
			>
				{/* Header interno: título/acción propios de Forecast */}
				<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
					<Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenImport}>
						{t("forecast.home.createImport")}
					</Button>
				</Stack>

				<Paper variant="outlined" elevation={0}>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>{t("forecast.home.name")}</TableCell>
									<TableCell>{t("forecast.home.origin")}</TableCell>
									<TableCell>{t("forecast.home.start")}</TableCell>
									<TableCell>{t("forecast.home.end")}</TableCell>
									<TableCell align="right">{t("forecast.home.actions")}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={5}>{t("common.loading")}</TableCell>
									</TableRow>
								) : errorMsg ? (
									<TableRow>
										<TableCell colSpan={5} style={{ color: "red" }}>
											{errorMsg}
										</TableCell>
									</TableRow>
								) : orderedRows.length === 0 ? (
									<TableRow>
										<TableCell colSpan={5}>{t("forecast.home.empty")}</TableCell>
									</TableRow>
								) : (
									orderedRows.map((row) => (
										<TableRow
											key={row.id}
											hover
											onClick={() => setSelectedForecastId(row.id)}
											sx={{ cursor: "pointer" }}
										>
											<TableCell>{row.nombre}</TableCell>
											<TableCell>{row.origen}</TableCell>
											<TableCell>{prettyDate(row.start_dtm)}</TableCell>
											<TableCell>{prettyDate(row.end_dtm)}</TableCell>
											<TableCell align="right" onClick={(e) => e.stopPropagation()}>
												<Stack direction="row" spacing={1} justifyContent="flex-end">
													<Tooltip title={t("forecast.home.edit")}>
														<IconButton size="small" onClick={() => handleEdit(row.id)}>
															<EditIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title={t("forecast.home.delete")}>
														<IconButton size="small" color="error" onClick={() => askDelete(row)}>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</Stack>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</Box>

			<ImportForecastModal
				open={openImport}
				onClose={() => setOpenImport(false)}
				onSaved={() => {
					setOpenImport(false);
					handleImported();
				}}
			/>

			{!!editId && (
				<EditForecastModal
					open={!!editId}
					fileId={editId}
					onClose={handleCloseEdit}
					onSaved={handleEdited}
				/>
			)}

			<Dialog open={confirmOpen} onClose={cancelDelete}>
				<DialogTitle>{t("forecast.home.deleteTitle")}</DialogTitle>
				<DialogContent>
					<Typography>
						{t("forecast.home.deleteMsg")} <strong>{toDelete?.nombre}</strong>?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelDelete}>{t("common.cancel")}</Button>
					<Button color="error" variant="contained" onClick={confirmDelete}>
						{t("common.delete")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
